import React from "react";
import { SxProps, Theme, Typography } from "@mui/material";
import { COLOR_CONSTANTS, TYPOGRAPHY_SCALE } from "../../constants/styles";
import { OverflowTooltip } from "../OverflowTooltip";

interface LabelProps {
  /**
   * 라벨 텍스트
   */
  children: React.ReactNode;
  /**
   * 커스텀 스타일
   */
  sx?: SxProps<Theme>;
  /**
   * 라벨이 잘렸을 때 hover 팝업으로 보여줄 본문.
   * `children` 이 문자열이면 생략해도 그 값이 쓰인다 — 노드일 때만 필요하다.
   */
  tooltipText?: string;
}

/**
 * Label 컴포넌트
 *
 * DataCell에서 사용하는 고정 스타일의 라벨 컴포넌트입니다.
 * typography/subtitle2 스타일을 사용합니다.
 *
 * 라벨이 칸을 넘치면 hover 팝업으로 전체 내용을 보여줍니다 — 잘렸을 때만 열립니다.
 * 팝업 본문은 문자열 `children` 자신, 또는 호출부가 준 `tooltipText` 입니다. `children` 이
 * 노드인데 `tooltipText` 가 없으면 본문이 없으므로 말줄임만 하고 팝업은 붙이지 않습니다.
 */
export const Label: React.FC<LabelProps> = ({ children, sx, tooltipText }) => {
  const labelSx = {
    ...TYPOGRAPHY_SCALE.subtitle2,
    color: COLOR_CONSTANTS.COLOR.GREY[600],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    ...sx,
  };

  // 노드 라벨을 팝업 본문으로 복제하면 노드가 이미 품은 팝업과 중첩되므로 복제하지 않는다.
  const popupText =
    tooltipText ?? (typeof children === "string" ? children : undefined);

  if (popupText === undefined) {
    return (
      <Typography component="label" noWrap sx={labelSx}>
        {children}
      </Typography>
    );
  }

  return (
    <OverflowTooltip
      component="label"
      text={children}
      popupText={popupText}
      sx={labelSx}
    />
  );
};

export type { LabelProps };
